import { useEffect, useMemo, useState } from 'react';
import {
  DEFAULT_OUTREACH_CONFIG,
  subscribeToOutreachConfig,
  subscribeToRecentWelcomes,
  subscribeToWaitingMembers,
} from '@/services/outreach';
import { sortWaiting } from '@/utils/outreach';
import type { OutreachConfig, OutreachRecord } from '@/types/outreach';

export interface OutreachTrayData {
  config: OutreachConfig;
  /** Members waiting for a hello, longest wait first. */
  waiting: OutreachRecord[];
  /** Latest welcomes (who welcomed whom). */
  recent: OutreachRecord[];
  loading: boolean;
  /**
   * Show the New members section: the feature is on, or there is something to show
   * (hand-added members, recent welcomes). Otherwise leaders see the tray unchanged.
   */
  visible: boolean;
}

/**
 * Live data for the leader tray. Subscribes only while `active` (leaders and
 * admins), so ordinary members never open these listeners.
 */
export function useOutreachTray(active: boolean): OutreachTrayData {
  const [config, setConfig] = useState<OutreachConfig>(DEFAULT_OUTREACH_CONFIG);
  const [waitingRaw, setWaitingRaw] = useState<OutreachRecord[]>([]);
  const [recent, setRecent] = useState<OutreachRecord[]>([]);
  const [loaded, setLoaded] = useState({ waiting: false, recent: false });

  useEffect(() => {
    if (!active) {
      setWaitingRaw([]);
      setRecent([]);
      setLoaded({ waiting: false, recent: false });
      return;
    }
    const unsubs = [
      subscribeToOutreachConfig(setConfig),
      subscribeToWaitingMembers(
        (records) => {
          setWaitingRaw(records);
          setLoaded((l) => ({ ...l, waiting: true }));
        },
        () => setLoaded((l) => ({ ...l, waiting: true }))
      ),
      subscribeToRecentWelcomes((records) => {
        setRecent(records);
        setLoaded((l) => ({ ...l, recent: true }));
      }),
    ];
    return () => unsubs.forEach((u) => u());
  }, [active]);

  const waiting = useMemo(() => sortWaiting(waitingRaw), [waitingRaw]);

  return {
    config,
    waiting,
    recent,
    loading: active && !(loaded.waiting && loaded.recent),
    visible: active && (config.enabled || waiting.length > 0 || recent.length > 0),
  };
}
